import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  isAllowedNavatticUrl,
  openNavatticDemoOrFallback,
  preloadNavatticDemo,
  resolveNavatticDemo,
} from "../navattic";

export type UseNavatticDemoOptions = {
  url: string | undefined;
  title?: string;
  /** Mobile flow of the same demo (same title); `url` is used when missing or invalid. */
  mobileUrl?: string;
  allow?: string;
  /** Lock page scroll while the demo is open. Default `true`. */
  lockScroll?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export type UseNavatticDemoResult = {
  /** `false` when `url` is missing or not an allowed Navattic URL. */
  isValid: boolean;
  isOpen: boolean;
  /** `true` between the click and the lightbox opening (script download). */
  isLoading: boolean;
  /** Pass the click event's `currentTarget` so focus returns to it on close. */
  open: (trigger?: HTMLElement | null) => Promise<void>;
  /** Fire-and-forget: download the embed script ahead of a click (hover/focus). */
  preload: () => void;
  /** The demo a click would open right now (mobile or desktop). Browser only. */
  resolve: () => { url: unknown; title?: string };
};

/**
 * Thin React wrapper around `@repo/utils/navattic` for trigger components.
 * All the heavy lifting (script loading, scroll lock, focus return, fallback)
 * lives in the framework-agnostic core so plain `onClick` handlers get the same
 * behaviour.
 */
export function useNavatticDemo({
  url,
  title,
  mobileUrl,
  allow,
  lockScroll,
  onOpen,
  onClose,
}: UseNavatticDemoOptions): UseNavatticDemoResult {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // The desktop demo is the required one; the mobile one only replaces it.
  const isValid = useMemo(() => isAllowedNavatticUrl(url), [url]);

  // Aborted on unmount so an open still waiting for embeds.js cannot show the
  // demo on the page the visitor navigated to in the meantime.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => controller.abort();
  }, []);

  const resolve = useCallback(
    () => resolveNavatticDemo({ url, title, mobileUrl }),
    [url, title, mobileUrl],
  );

  const open = useCallback(
    async (trigger?: HTMLElement | null) => {
      if (!isValid) return;
      const demo = resolve();
      if (!isAllowedNavatticUrl(demo.url)) return;

      setIsLoading(true);
      try {
        await openNavatticDemoOrFallback({
          url: demo.url,
          title: demo.title,
          allow,
          lockScroll,
          trigger,
          signal: abortRef.current?.signal,
          onOpen: () => {
            setIsOpen(true);
            onOpen?.();
          },
          onClose: () => {
            setIsOpen(false);
            onClose?.();
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [isValid, resolve, allow, lockScroll, onOpen, onClose],
  );

  const preload = useCallback(() => {
    if (!isValid) return;
    const demo = resolve();
    if (!isAllowedNavatticUrl(demo.url)) return;
    void preloadNavatticDemo({ url: demo.url, title: demo.title, allow }).catch(
      () => {
        // Preload is best-effort; the click path reports real failures.
      },
    );
  }, [isValid, resolve, allow]);

  return { isValid, isOpen, isLoading, open, preload, resolve };
}
